import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import {
  documentRefs,
  dokumentStatusLabel,
  dokumentStatusWert,
  formatDocumentDate,
  formatDocumentSize,
  brancheWahlListe,
  brancheWahlLabel,
} from "@kmvs/forms-core";
import { BRAND } from "./tokens";
import { sectionColorHex } from "./sectionColor";
import type {
  Answers,
  FieldDef,
  FieldType,
  FirmaEntry,
  PersonEntry,
  QuestionDef,
  SectionDef,
  VerantEntry,
} from "./types";

const useStyles = makeStyles({
  intro: {
    marginTop: 0,
    marginBottom: "12px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.muted,
  },
  sectionCard: {
    marginBottom: "18px",
    backgroundColor: BRAND.cardBg,
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "solid", BRAND.border),
    overflow: "hidden",
  },
  sectionHead: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    color: BRAND.white,
  },
  sectionHeadTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.1rem",
    fontWeight: 400,
    flexGrow: 1,
  },
  editBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "rgba(255,255,255,0.18)",
    backgroundImage: "none",
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 700,
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.border("1px", "solid", "rgba(255,255,255,0.4)"),
    ...shorthands.borderRadius("4px"),
    ":hover": {
      backgroundColor: "rgba(255,255,255,0.28)",
    },
  },
  subStep: {
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.borderTop("1px", "solid", BRAND.borderDashed),
  },
  subStepTitle: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: BRAND.muted,
    marginBottom: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  inlineEdit: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "transparent",
    backgroundImage: "none",
    color: BRAND.purple,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.75rem",
    fontWeight: 600,
    paddingTop: "2px",
    paddingBottom: "2px",
    paddingLeft: "6px",
    paddingRight: "6px",
    ...shorthands.border("0"),
    ":hover": {
      textDecorationLine: "underline",
    },
  },
  qaRow: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "2px",
    paddingTop: "6px",
    paddingBottom: "6px",
    "& > *": { minWidth: 0 },
    "@media (min-width: 720px)": {
      gridTemplateColumns: "1fr 2fr",
      columnGap: "16px",
    },
  },
  qaQuestion: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    color: BRAND.ink,
  },
  qaAnswer: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.ink,
    fontWeight: 600,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  qaEmpty: {
    color: BRAND.muted,
    fontStyle: "italic",
    fontWeight: 400,
  },
  empty: {
    paddingLeft: "14px",
    paddingRight: "14px",
    paddingTop: "10px",
    paddingBottom: "16px",
    fontSize: "0.85rem",
    color: BRAND.muted,
  },
});

/**
 * Die Feldtypen, für die diese Ansicht eine eigene Formatierung hat —
 * Prüfmass des Abdeckungstests (`SummaryView.test.tsx`) gegen `FIELD_TYPES`
 * aus forms-core.
 *
 * **Warum die Zusammenfassung einen eigenen Wächter braucht.** Sie ist ein
 * VIERTER `field.type`-Zweig neben den drei Renderern — und war der einzige,
 * den das Sicherheitsnetz nicht erfasste. Ihr Rückfall war `String(value)`:
 * `personEntries`, `verantEntries` (je zwei bzw. ein Feld in BEIDEN Schemata)
 * und jeder künftige Objekt-Feldtyp standen hier als „[object Object]" — auf
 * der letzten Seite vor dem Absenden, wo es niemand mehr korrigiert.
 *
 * `text`, `textarea`, `number` und `date` stehen mit drin, obwohl sie keinen
 * eigenen Zweig haben: sie sind der Skalar-Rückfall am Ende der Kette. Alles
 * andere muss vorher abgebogen sein.
 */
export const SUMMARY_BEHANDELTE_FELDTYPEN: ReadonlySet<FieldType> = new Set<FieldType>([
  "text",
  "textarea",
  "number",
  "date",
  "yesno",
  "select",
  "checkboxes",
  "iconCards",
  "personEntries",
  "verantEntries",
  "firmaEntries",
  "contactPicker",
  "documents",
  "dokumentStatus",
  "branchen",
]);

/** Optionswert → Anzeigebegriff, sonst der Rohwert (ein Schema kann älter sein als die Antwort). */
const optionLabel = (
  optionen: FieldDef["options"] | FieldDef["functionOptions"],
  wert: unknown
): string => {
  const roh = typeof wert === "string" ? wert : "";
  if (!roh) return "";
  return optionen?.find((o) => o.value === roh)?.label ?? roh;
};

/** „Max Mustermann — Geschäftsführer (50 %)"; leere Bestandteile fallen weg. */
const personZeile = (field: FieldDef, eintrag: PersonEntry): string => {
  const name = [eintrag.vorname, eintrag.nachname].filter(Boolean).join(" ").trim();
  const funktion = optionLabel(field.functionOptions, eintrag.funktion);
  const kopf = [name, funktion].filter(Boolean).join(" — ");
  const anteile = eintrag.anteile ? `${eintrag.anteile} %` : "";
  return anteile ? `${kopf} (${anteile})`.trim() : kopf;
};

/** „Steuerberatung — Muster GmbH" bzw. „… — Max Mustermann" je nach `typ`. */
const verantZeile = (field: FieldDef, eintrag: VerantEntry): string => {
  const funktion = optionLabel(field.functionOptions, eintrag.funktion);
  const wer =
    eintrag.typ === "extern"
      ? eintrag.firmenname ?? ""
      : [eintrag.vorname, eintrag.nachname].filter(Boolean).join(" ").trim();
  return [funktion, wer].filter(Boolean).join(" — ");
};

/**
 * Eine Verbund-/Beteiligungsfirma als eine Zeile.
 *
 * Firmierung, Verbindung und Quote — das ist, was die Zusammenfassung
 * beantworten muss („welche Firmen gehören dazu und wie?"). Rolle und
 * Tätigkeit bleiben weg: Sie machen aus jeder Firma einen Absatz, und die
 * Seite ist eine Übersicht vor dem Absenden, kein zweites Formular.
 */
const firmaZeile = (field: FieldDef, eintrag: FirmaEntry): string => {
  const verbindung = optionLabel(field.options, eintrag.verbindung);
  const quote = (eintrag.anteil ?? "").trim();
  return [
    (eintrag.firmenname ?? "").trim(),
    verbindung,
    quote ? `${quote} %` : "",
  ]
    .filter(Boolean)
    .join(" — ");
};

/** Objekt-Einträge einer Liste, ohne an einem Fremdwert zu zerbrechen. */
const eintraege = <T,>(value: unknown): T[] =>
  Array.isArray(value)
    ? (value.filter((e) => e && typeof e === "object" && !Array.isArray(e)) as T[])
    : [];

export const formatAnswerForField = (field: FieldDef, value: unknown): string => {
  if (value == null || value === "") return "";
  if (field.type === "yesno") {
    if (value === "ja") return "Ja";
    if (value === "nein") return "Nein";
    return String(value);
  }
  if (field.type === "select") {
    return optionLabel(field.options, value) || String(value);
  }
  // `iconCards` ist wie `checkboxes` eine Mehrfachauswahl über `options` —
  // getrennt behandelt landete es im Skalar-Rückfall und stand als „a,b" da.
  if (field.type === "checkboxes" || field.type === "iconCards") {
    if (!Array.isArray(value)) return "";
    return value
      .map((v) => optionLabel(field.options, v) || String(v))
      .filter(Boolean)
      .join(", ");
  }
  if (field.type === "contactPicker") {
    if (!value || typeof value !== "object") return "";
    const v = value as { label?: string; email?: string };
    return [v.label, v.email].filter(Boolean).join(" · ");
  }
  if (field.type === "personEntries") {
    return eintraege<PersonEntry>(value)
      .map((eintrag) => personZeile(field, eintrag))
      .filter(Boolean)
      .join("\n");
  }
  if (field.type === "verantEntries") {
    return eintraege<VerantEntry>(value)
      .map((eintrag) => verantZeile(field, eintrag))
      .filter(Boolean)
      .join("\n");
  }
  if (field.type === "firmaEntries") {
    return eintraege<FirmaEntry>(value)
      .map((eintrag) => firmaZeile(field, eintrag))
      .filter(Boolean)
      .join("\n");
  }
  if (field.type === "documents") {
    // Ohne diesen Zweig zeigte der Rückfall unten „[object Object]" — die
    // Zusammenfassung ist die letzte Seite vor dem Absenden, ausgerechnet dort
    // müssen die hochgeladenen Unterlagen mit Namen dastehen.
    const dateien = documentRefs(value);
    if (dateien.length === 0) return "";
    return dateien
      .map((ref) => {
        const groesse = formatDocumentSize(ref.sizeBytes);
        return groesse ? `${ref.fileName} (${groesse})` : ref.fileName;
      })
      .join("\n");
  }
  if (field.type === "branchen") {
    // Der mitgespeicherte Anzeigepfad — die Zusammenfassung hat den Katalog
    // nicht und könnte die Id sonst nicht lesbar machen.
    return brancheWahlListe(value).map(brancheWahlLabel).join("\n");
  }
  if (field.type === "dokumentStatus") {
    // „liegt vor · 12.03.2026". Ohne Stand keine Zeile: dreizehn Papiere mit
    // „nicht eingetragen" wären die längste Liste der Zusammenfassung und die
    // mit dem geringsten Inhalt.
    const stand = dokumentStatusWert(value);
    if (!stand.status) return "";
    const datum = stand.status === "liegt_vor" ? formatDocumentDate(stand.datum) : "";
    return [dokumentStatusLabel(stand.status), datum].filter(Boolean).join(" · ");
  }

  /**
   * Der Skalar-Rückfall: `text`, `textarea`, `number`, `date` — und alles,
   * was aus einem NEUEREN Schema hier ankommt.
   *
   * Ein Objekt darf hier nie mehr durchlaufen: `String({…})` ergibt
   * „[object Object]", und genau das stand bis zu diesem Stand für jeden
   * Listen-Feldtyp in der Zusammenfassung. Lieber gar keine Zeile als eine
   * falsche — im Formular selbst zeigt `QuestionField` für einen unbekannten
   * Feldtyp ohnehin den lauten Rückfall, der Kunde erfährt also davon. Ein
   * unbekannter Typ mit Skalarwert wird dagegen weiter angezeigt: sein Wert
   * ist lesbar, und ihn zu verschweigen wäre der grössere Schaden.
   */
  if (typeof value === "object") return "";
  return String(value);
};

const formatAnswer = (q: QuestionDef, answers: Answers): { label: string; text: string }[] => {
  const fields = q.fields ?? (q.field ? [q.field] : []);
  const rows: { label: string; text: string }[] = [];
  for (const f of fields) {
    const text = formatAnswerForField(f, answers[f.key]);
    if (text) rows.push({ label: f.label ?? f.key, text });
  }
  if (q.followUpFields) {
    for (const f of q.followUpFields) {
      const text = formatAnswerForField(f, answers[f.key]);
      if (text) rows.push({ label: f.label ?? f.key, text });
    }
  }
  return rows;
};

type Props = {
  sections: SectionDef[];
  /** The Zusammenfassung section index (so we don't include it in the review). */
  zusammenfassungSectionIdx: number;
  answers: Answers;
  onJumpTo: (sectionIdx: number, subStepIdx: number) => void;
};

const SummaryView = ({ sections, zusammenfassungSectionIdx, answers, onJumpTo }: Props) => {
  const styles = useStyles();
  return (
    <div>
      <p className={styles.intro}>
        Bitte prüfen Sie Ihre Angaben. Sie können jeden Abschnitt direkt zur Korrektur
        anspringen, bevor Sie die Risikoanalyse absenden.
      </p>
      {sections.map((section, sIdx) => {
        if (sIdx === zusammenfassungSectionIdx) return null;
        const accent = sectionColorHex[section.color];
        return (
          <div key={section.key} className={styles.sectionCard}>
            <div className={styles.sectionHead} style={{ backgroundColor: accent }}>
              <span className={styles.sectionHeadTitle}>{section.title}</span>
              <button
                type="button"
                className={styles.editBtn}
                onClick={() => onJumpTo(sIdx, 0)}
              >
                Bearbeiten
              </button>
            </div>
            {section.subSteps.map((sub, ssIdx) => {
              if (sub.customKind) return null;
              const rows: { question: string; pairs: { label: string; text: string }[] }[] = [];
              for (const q of sub.questions) {
                const pairs = formatAnswer(q, answers);
                if (pairs.length > 0) rows.push({ question: q.question, pairs });
              }
              return (
                <div key={sub.key} className={styles.subStep}>
                  <div className={styles.subStepTitle}>
                    <span>{sub.title}</span>
                    <button
                      type="button"
                      className={styles.inlineEdit}
                      onClick={() => onJumpTo(sIdx, ssIdx)}
                    >
                      Bearbeiten →
                    </button>
                  </div>
                  {rows.length === 0 ? (
                    <div className={mergeClasses(styles.qaAnswer, styles.qaEmpty)}>
                      keine Angaben
                    </div>
                  ) : (
                    rows.map((row, rIdx) => (
                      <div key={rIdx}>
                        <div className={styles.qaQuestion}>{row.question}</div>
                        {row.pairs.map((p, i) => (
                          <div key={i} className={styles.qaRow}>
                            <span className={styles.qaQuestion}>{p.label}</span>
                            <span className={styles.qaAnswer}>{p.text}</span>
                          </div>
                        ))}
                      </div>
                    ))
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default SummaryView;
